import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Button,
  Typography,
  DatePicker,
  Alert,
  message,
} from "antd";
import {
  UserOutlined,
  ShoppingCartOutlined,
  DollarCircleOutlined,
  RiseOutlined,
  DownloadOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  ShopOutlined,
  ReloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  FireOutlined,
  RocketOutlined,
  ScheduleOutlined,
  TagOutlined,
  DashboardOutlined,
  BarChartOutlined,
  EyeOutlined,
  AppstoreOutlined,
  GlobalOutlined,
  SafetyOutlined,
  SolutionOutlined,
  InboxOutlined,
  StarOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Everything on this page comes from a single counts-only endpoint. Detail lists live on
// the screens each tile links to - the dashboard never downloads rows.
const OVERVIEW_PATH = "api/AdminDashboard/GetOverview";

const EMPTY_OVERVIEW = {
  HighIntentLeads: 0,
  DirectRegistrations: 0,
  PendingUserApprovals: 0,
  PendingReactivations: 0,
  TotalOrders: 0,
  TotalQuantity: 0,
  TotalRevenue: 0,
  PrevTotalRevenue: 0,
  TotalDownloads: 0,
  TotalUsers: 0,
  ActiveUsers: 0,
  TotalProducts: 0,
  ActiveProducts: 0,
  InactiveProducts: 0,
  UnpublishedProducts: 0,
  TodayIndiaMartLeads: 0,
  TodayTradeIndiaLeads: 0,
  RangeIndiaMartLeads: 0,
  RangeTradeIndiaLeads: 0,
  VisitorEvents: 0,
  UniqueVisitors: 0,
  TodaysVisitors: 0,
};

// Survives navigating away and back (e.g. dashboard -> High Intent Leads -> dashboard),
// so the last figures paint instantly while fresh ones load.
const overviewCache = new Map();

const defaultRange = () => [dayjs().subtract(29, "day"), dayjs()];
const rangeKey = (range) => `${range[0].format("DD-MM-YYYY")}|${range[1].format("DD-MM-YYYY")}`;

const getRangePresets = () => [
  { label: "Today", value: [dayjs(), dayjs()] },
  { label: "Last 7 days", value: [dayjs().subtract(6, "day"), dayjs()] },
  { label: "Last 30 days", value: [dayjs().subtract(29, "day"), dayjs()] },
  { label: "This month", value: [dayjs().startOf("month"), dayjs()] },
];

const getAuthToken = () => {
  const keys = ["token", "accessToken", "AuthToken", "jwtToken"];
  for (const k of keys) {
    const v = localStorage.getItem(k);
    if (v) return v.replace(/^"|"$/g, "");
  }
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.token || user?.accessToken || null;
  } catch {
    return null;
  }
};

const formatRupees = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

const StatTile = ({ title, value, icon, color, loading, onClick, formatter, footer }) => (
  <Card
    hoverable={!!onClick}
    onClick={onClick}
    style={{ height: "100%", cursor: onClick ? "pointer" : "default", borderTop: `3px solid ${color}` }}
    styles={{ body: { padding: 16 } }}
  >
    <Statistic
      title={title}
      value={value}
      loading={loading}
      formatter={formatter}
      prefix={<span style={{ color, marginRight: 4 }}>{icon}</span>}
      valueStyle={{ color, fontWeight: 600 }}
    />
    {footer && !loading && <div style={{ marginTop: 6 }}>{footer}</div>}
  </Card>
);

const SectionTitle = ({ children, extra }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", margin: "24px 0 12px" }}>
    <Title level={5} style={{ margin: 0, color: "#595959" }}>
      {children}
    </Title>
    {extra}
  </div>
);

const AdminDashBoard = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState(defaultRange);
  const [data, setData] = useState(() => overviewCache.get(rangeKey(defaultRange())) || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const latestRequest = useRef(0);

  const from = dateRange[0].format("DD-MM-YYYY");
  const to = dateRange[1].format("DD-MM-YYYY");

  const fetchOverview = useCallback(
    async ({ notify = false } = {}) => {
      const cacheKey = `${from}|${to}`;
      const requestId = ++latestRequest.current;

      // Never show one range's numbers under another range's heading: an uncached range
      // goes back to skeletons until its own figures arrive.
      setData(overviewCache.get(cacheKey) || null);
      setLoading(true);
      setError(null);

      try {
        const token = getAuthToken();
        const res = await axios.get(`${global.config.APIURL.BaseURL}${OVERVIEW_PATH}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { From: from, To: to },
        });
        if (requestId !== latestRequest.current) return; // a newer range was picked meanwhile

        const overview = { ...EMPTY_OVERVIEW, ...(res.data || {}) };
        overviewCache.set(cacheKey, overview);
        setData(overview);
        if (notify) message.success("Dashboard refreshed");
      } catch (err) {
        if (requestId !== latestRequest.current) return;
        console.error("Error fetching dashboard overview:", err);
        setError(
          err?.response?.status === 403
            ? "You don't have access to the admin dashboard."
            : "Couldn't load the dashboard. Please try again."
        );
      } finally {
        if (requestId === latestRequest.current) setLoading(false);
      }
    },
    [from, to]
  );

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  // Tiles show a skeleton until figures for the selected range exist; after that, refreshes
  // keep the current numbers on screen and just spin the Refresh button.
  const d = data || EMPTY_OVERVIEW;
  const firstLoad = !data;

  const revenueGrowth = d.PrevTotalRevenue
    ? ((d.TotalRevenue - d.PrevTotalRevenue) / d.PrevTotalRevenue) * 100
    : d.TotalRevenue > 0
    ? 100
    : 0;
  const growthColor = revenueGrowth >= 0 ? "#52c41a" : "#ff4d4f";
  const GrowthIcon = revenueGrowth >= 0 ? ArrowUpOutlined : ArrowDownOutlined;

  const activeRate = d.TotalUsers ? Math.round((d.ActiveUsers / d.TotalUsers) * 100) : 0;
  const pendingTotal =
    d.HighIntentLeads + d.DirectRegistrations + d.PendingUserApprovals + d.PendingReactivations;

  const quickActions = [
    { title: "Manage Product", icon: <ShopOutlined />, path: "/AdminCategory", color: "#52c41a" },
    { title: "All Products", icon: <AppstoreOutlined />, path: "/AdminAllProducts", color: "#1890ff" },
    { title: "Product Tags", icon: <TagOutlined />, path: "/ManageProductTags", color: "#fa8c16" },
    { title: "LastChance Deals", icon: <FireOutlined />, path: "/ManageLastChanceDeals", color: "#ff4d4f" },
    { title: "Trending Items", icon: <RiseOutlined />, path: "/ManageTrendingItems", color: "#13c2c2" },
    { title: "User List", icon: <UserOutlined />, path: "/UserList", color: "#eb2f96" },
    { title: "Product Insights", icon: <StarOutlined />, path: "/ProductInsights", color: "#faad14" },
    { title: "Activity Summary", icon: <BarChartOutlined />, path: "/UserActivitySummary", color: "#f5222d" },
    { title: "Catalogue Access", icon: <InboxOutlined />, path: "/TempAccessRequests", color: "#2f54eb" },
    { title: "Insta Post Schedule", icon: <ScheduleOutlined />, path: "/InstaPostSchedule", color: "#a0d911" },
  ];

  return (
    <div style={{ padding: 24, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header */}
      <Row gutter={[0, 12]} justify="space-between" align="middle" wrap>
        <Col xs={24} md={12}>
          <Title level={2} style={{ margin: 0, color: "#1890ff" }}>
            <DashboardOutlined style={{ marginRight: 8 }} />
            Admin Dashboard
          </Title>
          <Text type="secondary">
            Overview for {dateRange[0].format("DD MMM YYYY")} – {dateRange[1].format("DD MMM YYYY")}
          </Text>
        </Col>
        <Col xs={24} md={12} style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
          <RangePicker
            value={dateRange}
            onChange={(v) => v && v[0] && v[1] && setDateRange(v)}
            presets={getRangePresets()}
            format="DD-MM-YYYY"
            allowClear={false}
            disabledDate={(current) => current && current > dayjs().endOf("day")}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchOverview({ notify: true })}
            loading={loading && !!data}
            type="primary"
          >
            Refresh
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginTop: 16 }}
          action={
            <Button size="small" onClick={() => fetchOverview()}>
              Retry
            </Button>
          }
        />
      )}

      {/* Needs attention - always current, not affected by the date range */}
      <SectionTitle
        extra={
          !firstLoad && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {pendingTotal} item{pendingTotal === 1 ? "" : "s"} waiting
            </Text>
          )
        }
      >
        Needs Attention
      </SectionTitle>
      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <StatTile
            title="High Intent Leads"
            value={d.HighIntentLeads}
            icon={<FireOutlined />}
            color="#ff4d4f"
            loading={firstLoad}
            onClick={() => navigate("/LeadsToApprove")}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatTile
            title="Direct Registrations"
            value={d.DirectRegistrations}
            icon={<SolutionOutlined />}
            color="#13c2c2"
            loading={firstLoad}
            onClick={() => navigate("/DirectRegistrationRequests")}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatTile
            title="Pending User Approvals"
            value={d.PendingUserApprovals}
            icon={<ClockCircleOutlined />}
            color="#faad14"
            loading={firstLoad}
            onClick={() => navigate("/UserList")}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatTile
            title="Reactivation Requests"
            value={d.PendingReactivations}
            icon={<SafetyOutlined />}
            color="#eb2f96"
            loading={firstLoad}
            onClick={() => navigate("/ReactivationRequests")}
          />
        </Col>
      </Row>

      {/* Sales - selected range */}
      <SectionTitle>Sales &amp; Downloads</SectionTitle>
      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <StatTile
            title="Orders"
            value={d.TotalOrders}
            icon={<ShoppingCartOutlined />}
            color="#52c41a"
            loading={firstLoad}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatTile
            title="Revenue"
            value={d.TotalRevenue}
            icon={<DollarCircleOutlined />}
            color="#fa8c16"
            loading={firstLoad}
            formatter={formatRupees}
            footer={
              <Text style={{ fontSize: 12, color: growthColor }}>
                <GrowthIcon /> {Math.abs(revenueGrowth).toFixed(1)}% vs previous period
              </Text>
            }
          />
        </Col>
        <Col xs={12} md={6}>
          <StatTile
            title="Quantity Ordered"
            value={d.TotalQuantity}
            icon={<AppstoreOutlined />}
            color="#2f54eb"
            loading={firstLoad}
          />
        </Col>
        <Col xs={12} md={6}>
          <StatTile
            title="Downloads"
            value={d.TotalDownloads}
            icon={<DownloadOutlined />}
            color="#722ed1"
            loading={firstLoad}
          />
        </Col>
      </Row>

      {/* Customers, catalogue, leads, visitors */}
      <SectionTitle>Customers, Catalogue &amp; Traffic</SectionTitle>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          <Card
            title={<><TeamOutlined /> Customers</>}
            size="small"
            loading={firstLoad}
            hoverable
            onClick={() => navigate("/UserList")}
            style={{ height: "100%", cursor: "pointer" }}
          >
            <Row gutter={8}>
              <Col span={12}>
                <Statistic title="Total" value={d.TotalUsers} />
              </Col>
              <Col span={12}>
                <Statistic title="Active" value={d.ActiveUsers} valueStyle={{ color: "#52c41a" }} />
              </Col>
            </Row>
            <Progress percent={activeRate} strokeColor="#52c41a" size="small" style={{ marginTop: 8 }} />
            <Text type="secondary" style={{ fontSize: 12 }}>Active rate</Text>
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card
            title={<><AppstoreOutlined /> Catalogue</>}
            size="small"
            loading={firstLoad}
            hoverable
            onClick={() => navigate("/AdminAllProducts")}
            style={{ height: "100%", cursor: "pointer" }}
          >
            <Row gutter={8}>
              <Col span={12}>
                <Statistic title="Products" value={d.TotalProducts} valueStyle={{ color: "#1890ff" }} />
              </Col>
              <Col span={12}>
                <Statistic title="Active" value={d.ActiveProducts} valueStyle={{ color: "#52c41a" }} />
              </Col>
              <Col span={12} style={{ marginTop: 8 }}>
                <Statistic title="Inactive" value={d.InactiveProducts} valueStyle={{ fontSize: 18 }} />
              </Col>
              <Col span={12} style={{ marginTop: 8 }}>
                <Statistic title="Unpublished" value={d.UnpublishedProducts} valueStyle={{ fontSize: 18, color: "#faad14" }} />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card title={<><GlobalOutlined /> Leads</>} size="small" loading={firstLoad} style={{ height: "100%" }}>
            <Row gutter={8}>
              <Col span={12}>
                <Statistic title="IndiaMart today" value={d.TodayIndiaMartLeads} valueStyle={{ color: "#1890ff" }} />
              </Col>
              <Col span={12}>
                <Statistic title="TradeIndia today" value={d.TodayTradeIndiaLeads} valueStyle={{ color: "#fa8c16" }} />
              </Col>
              <Col span={12} style={{ marginTop: 8 }}>
                <Statistic title="IndiaMart in range" value={d.RangeIndiaMartLeads} valueStyle={{ fontSize: 18 }} />
              </Col>
              <Col span={12} style={{ marginTop: 8 }}>
                <Statistic title="TradeIndia in range" value={d.RangeTradeIndiaLeads} valueStyle={{ fontSize: 18 }} />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <Card
            title={<><EyeOutlined /> Visitors</>}
            size="small"
            loading={firstLoad}
            hoverable
            onClick={() => navigate("/UserActivitySummary")}
            style={{ height: "100%", cursor: "pointer" }}
          >
            <Row gutter={8}>
              <Col span={12}>
                <Statistic title="Today" value={d.TodaysVisitors} valueStyle={{ color: "#1890ff" }} />
              </Col>
              <Col span={12}>
                <Statistic title="Unique in range" value={d.UniqueVisitors} valueStyle={{ color: "#52c41a" }} />
              </Col>
              <Col span={24} style={{ marginTop: 8 }}>
                <Statistic title="Page events in range" value={d.VisitorEvents} valueStyle={{ fontSize: 18 }} />
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Quick Actions - navigation only, no data */}
      <SectionTitle>
        <RocketOutlined /> Quick Actions
      </SectionTitle>
      <Row gutter={[12, 12]}>
        {quickActions.map((item) => (
          <Col xs={12} sm={8} md={6} lg={4} key={item.path}>
            <Card
              hoverable
              size="small"
              onClick={() => navigate(item.path)}
              style={{ textAlign: "center", borderColor: item.color }}
            >
              <div style={{ color: item.color, fontSize: 22, marginBottom: 4 }}>{item.icon}</div>
              <Text strong style={{ fontSize: 12 }}>
                {item.title}
              </Text>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default AdminDashBoard;
